import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BookOpen, TrendingUp, Megaphone, PartyPopper, AlertTriangle } from 'lucide-react';

interface HeroBannerNotice {
  id: string;
  title: string;
  content: string;
  type: 'notice' | 'event' | 'maintenance';
  imageUrl?: string;
  linkUrl?: string;
}

const TYPE_META: Record<HeroBannerNotice['type'], { label: string; Icon: typeof Megaphone; badge: string }> = {
  notice: { label: '공지', Icon: Megaphone, badge: 'bg-sky-500 text-white' },
  event: { label: '이벤트', Icon: PartyPopper, badge: 'bg-emerald-500 text-white' },
  maintenance: { label: '점검', Icon: AlertTriangle, badge: 'bg-rose-500 text-white' },
};

interface Props {
  notice: HeroBannerNotice | null;
}

export function HeroBanner({ notice }: Props) {
  // 활성 공지가 없으면 기본 환영 메시지 (서비스 첫인상 보장)
  if (!notice) {
    return (
      <section className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 p-6 md:p-10">
        <div className="relative z-10 space-y-4">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            SCP 재단 기밀 문서에 오신 것을 환영합니다
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            전 세계의 이상현상, 위험한 개체들을 격리·연구하는 비밀 조직 SCP 재단.
            그들의 숨겨진 이야기를 지금 확인하세요.
          </p>
          <div className="flex gap-2 pt-2">
            <Button variant="scp" size="lg" asChild>
              <Link to="/novels">
                <BookOpen className="mr-2 h-4 w-4" />
                작품 보기
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/ranking">
                <TrendingUp className="mr-2 h-4 w-4" />
                랭킹 보기
              </Link>
            </Button>
          </div>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-primary/20 to-transparent opacity-50 hidden md:block" />
      </section>
    );
  }

  const meta = TYPE_META[notice.type];
  const linkTo = notice.linkUrl || `/notices/${notice.id}`;
  const isExternal = !!notice.linkUrl && /^https?:\/\//.test(notice.linkUrl);

  // 이미지가 있으면 이미지 배너 (좌측 텍스트 + 우측 이미지)
  // 없으면 텍스트 배너 (그라데이션 + 아이콘)
  const Inner = (
    <section className="relative overflow-hidden rounded-lg border bg-gradient-to-r from-primary/10 to-primary/5 group">
      <div className="grid md:grid-cols-2 gap-0 items-stretch">
        <div className="p-6 md:p-10 space-y-4">
          <div className="flex items-center gap-2">
            <Badge className={meta.badge}>
              <meta.Icon className="h-3 w-3 mr-1" />
              {meta.label}
            </Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight line-clamp-2">
            {notice.title}
          </h1>
          <p className="text-muted-foreground line-clamp-3 max-w-lg whitespace-pre-line">
            {notice.content}
          </p>
          <div className="pt-2">
            <Button variant="scp" size="lg" className="group-hover:translate-x-0.5 transition-transform">
              자세히 보기 →
            </Button>
          </div>
        </div>
        {notice.imageUrl && (
          <div className="hidden md:block relative min-h-[220px]">
            <img
              src={notice.imageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    </section>
  );

  return isExternal ? (
    <a href={notice.linkUrl} target="_blank" rel="noopener noreferrer" className="block">
      {Inner}
    </a>
  ) : (
    <Link to={linkTo} className="block">
      {Inner}
    </Link>
  );
}
